import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import VUSeal from './VUSeal'
import Orientation from './Orientation'
import { MODULES, RESOURCES, CLAUDE_PROJECT_LINK } from '../constants'
import { supabase } from '../supabaseClient'
import './Dashboard.css'

export default function Dashboard() {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [progress, setProgress] = useState([])
  const [loading, setLoading] = useState(true)
  const [showOrientation, setShowOrientation] = useState(false)

  useEffect(() => {
    async function load() {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) { navigate('/login'); return }
      setUser(session.user)

      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single()
      setProfile(profileData)

      // Show orientation if first login
      const hasSeenOrientation = localStorage.getItem(`vu_oriented_${session.user.id}`)
      if (!hasSeenOrientation) setShowOrientation(true)

      const { data: progressData } = await supabase
        .from('module_progress')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('curriculum', 'branding')
      setProgress(progressData || [])
      setLoading(false)
    }
    load()
  }, [navigate])

  const handleOrientationComplete = () => {
    if (user) localStorage.setItem(`vu_oriented_${user.id}`, 'true')
    setShowOrientation(false)
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    navigate('/')
  }

  const completedModules = progress.filter(p => p.completed_at).length
  const displayName = profile?.full_name || user?.email || 'Welcome'
  const displayProperty = profile?.property || ''

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#0D3B66', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: '#A7D8F5', fontFamily: 'Georgia, serif', fontSize: '16px', fontStyle: 'italic' }}>Loading your dashboard...</p>
    </div>
  )

  if (showOrientation) return <Orientation onComplete={handleOrientationComplete} />

  return (
    <div className="dash">
      <nav className="dash-nav">
        <span className="dash-nav-logo">VACATIA UNIVERSITY</span>
        <div className="dash-nav-right">
          <button className="dash-nav-cohort" onClick={() => navigate('/cohort')}>Our Cohort</button>
          <span className="dash-nav-user">{displayProperty}</span>
          <button className="dash-nav-signout" onClick={handleSignOut}>Sign out</button>
        </div>
      </nav>

      <header className="dash-header">
        <div className="dash-header-inner">
          <div className="dash-seal"><VUSeal size={80} /></div>
          <div>
            <p className="dash-welcome-eyebrow">Welcome back</p>
            <h1 className="dash-welcome-name">{displayName}</h1>
            <p className="dash-welcome-property">{displayProperty}</p>
          </div>
        </div>
        <div className="dash-progress-bar-wrap">
          <div className="dash-progress-label">
            <span>Curriculum 01: Branding</span>
            <span>{completedModules} of 8 modules complete</span>
          </div>
          <div className="dash-progress-track">
            <div className="dash-progress-fill" style={{ width: `${(completedModules / 8) * 100}%` }} />
          </div>
        </div>
      </header>

      <main className="dash-main">
        <section className="dash-section">
          <p className="dash-section-label">Curriculum 01 - Branding</p>
          <h2 className="dash-section-heading">Your modules</h2>
          <div className="dash-modules">
            {MODULES.map((mod, idx) => {
              const modProgress = progress.find(p => p.module_number === idx + 1)
              const isComplete = !!modProgress?.completed_at
              const hasAdminNote = !!modProgress?.admin_note
              const isCurrent = idx === completedModules
              const isLocked = idx > completedModules
              return (
                <div
                  key={mod.number}
                  className={`dash-module ${isComplete ? 'complete' : ''} ${isCurrent ? 'current' : ''} ${isLocked ? 'locked' : ''}`}
                  onClick={() => !isLocked && navigate(`/module/${idx + 1}`)}
                  style={{ cursor: isLocked ? 'default' : 'pointer' }}
                >
                  <div className="dash-module-num">{isComplete ? '✓' : mod.number}</div>
                  <div className="dash-module-body">
                    <h3 className="dash-module-title">{mod.title}</h3>
                    <p className="dash-module-desc">{mod.description}</p>
                    {hasAdminNote && <p className="dash-admin-note-flag">● Note from Sam</p>}
                  </div>
                  <div className="dash-module-action">
                    {isComplete && <span className="dash-tag complete">Complete</span>}
                    {isCurrent && <span className="dash-tag current">Open →</span>}
                    {isLocked && <span className="dash-tag locked">Upcoming</span>}
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        <section className="dash-section">
          <p className="dash-section-label">Resource Library</p>
          <h2 className="dash-section-heading">Your brand toolkit</h2>
          <p className="dash-resource-intro">These resources are yours to keep. Return to them anytime - during a module, before a board meeting, or when you need to communicate on behalf of the brand.</p>
          <div className="dash-resources">
            <a href={RESOURCES.brand_resources.link} target="_blank" rel="noreferrer" className="dash-resource-card">
              <div className="dash-resource-icon">{RESOURCES.brand_resources.icon}</div>
              <div className="dash-resource-body">
                <h3 className="dash-resource-title">{RESOURCES.brand_resources.title}</h3>
                <p className="dash-resource-desc">{RESOURCES.brand_resources.description}</p>
              </div>
              <span className="dash-resource-arrow">→</span>
            </a>
            <a href={CLAUDE_PROJECT_LINK} target="_blank" rel="noreferrer" className="dash-resource-card featured">
              <div className="dash-resource-icon">💬</div>
              <div className="dash-resource-body">
                <h3 className="dash-resource-title">Vacatia Claude Project</h3>
                <p className="dash-resource-desc">Type any situation - guest message, owner email, board communication - and get back an on-brand response. Requires Claude enterprise access - reach out to Sam if you need an invite.</p>
              </div>
              <span className="dash-resource-arrow">→</span>
            </a>
          </div>
        </section>
      </main>

      <footer className="dash-footer">
        <p>Vacatia University &nbsp;·&nbsp; Est. 2026 &nbsp;·&nbsp; <em>Leave better than you arrived.</em></p>
      </footer>
    </div>
  )
}
